import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DataService } from '@app-core/services/data/data.service';
import { TranslateService } from '@ngx-translate/core';
import { configEvents } from '@app-asset-config/models/tagging.model';
import { StorageService } from '@app-core/services/storage/storage.service';
import { EXCLUDED_EVENTS, VIDEO_TYPE_LIST } from '@app-core/constants/constants';
import { EVENTS_CONFIG } from '@app-core/constants/constants';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Subject } from 'rxjs';
import { AssetConfigurationService } from '@app-asset-config/services/asset-configuration.service';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';
@Component({
  selector: 'app-in-person-escalation',
  templateUrl: './in-person-escalation.component.html',
  styleUrls: ['./in-person-escalation.component.scss'],
})
export class InPersonEscalationComponent implements OnInit {
  public fleetId: string;
  public eventsConfig: configEvents[];
  public othersConfig: configEvents[];
  public customEvents: configEvents[];
  public valuesChanged: boolean = false;
  public driverFacingEvents: configEvents[];
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public loginName: string;
  public coachingThersholdDirty: any;
  public isEventsTypeValid: boolean = true;
  public isCoachingThresholdFormValid: boolean = true;
  public currentMetricUnit: string;
  private unsubscribeOnDestroy: Subject<void> = new Subject<void>();
  public disableEventSelection = false;
  private previousFormValue: {
    enableInPersonEscalation: boolean;
    missedSelfCoachingSessions: number;
    minConsecutiveSelfCoachingSessions: number;
  };

  @Input()
  public escalationEventTypes: string[] = [];
  @Input()
  public coachConfigLoader: boolean = true;
  @Input()
  public enableSelfCoaching: boolean;
  @Input()
  public enableInPersonEscalation: boolean;
  @Input()
  public enableAutoSelection: boolean;
  @Input()
  public missedSelfCoachingSessions: number = 3;
  @Input()
  public minConsecutiveSelfCoachingSessions: number = 4;
  @Input()
  public inPersonEscalationLoader: boolean = false;

  @Output()
  public inPersonEscalationValues = new EventEmitter<any>();
  public inPersonEscalationThresholdForm = new FormGroup({
    enableInPersonEscalation: new FormControl(false),
    missedSelfCoachingSessions: new FormControl(null, [Validators.required, Validators.min(1), Validators.max(10), this.numberValidator]),
    minConsecutiveSelfCoachingSessions: new FormControl(null, [
      Validators.required,
      Validators.min(2),
      Validators.max(5),
      this.numberValidator,
    ]),
  });

  constructor(
    public dataService: DataService,
    public translate: TranslateService,
    private storageService: StorageService,
    private assetService: AssetConfigurationService,
    private mixpanelService: MixPanelService
  ) {}

  public ngOnInit(): void {
    const combinedEventsList = this.dataService.modifyFleeEvents();
    const modifiedEventsConfig = this.dataService.transformObject(combinedEventsList);

    const filteredCustomData = {};
    const filterStandardEvents = {};
    for (const key in modifiedEventsConfig) {
      if (!(key in EVENTS_CONFIG)) {
        filteredCustomData[key] = modifiedEventsConfig[key];
      } else {
        filterStandardEvents[key] = modifiedEventsConfig[key];
      }
    }

    const otherConfigList = VIDEO_TYPE_LIST.filter((obj) => obj.value === 'externalEvents');

    this.othersConfig = this.processConfig(otherConfigList).filter((event) => !EXCLUDED_EVENTS.includes(event.value));
    this.eventsConfig = this.processConfig(filterStandardEvents).filter((event) => !EXCLUDED_EVENTS.includes(event.key));
    this.customEvents = this.processConfig(filteredCustomData).filter(
      (event) => event.key !== 'PotentialCrash' && event.label !== 'High-G Event' && !EXCLUDED_EVENTS.includes(event.key)
    );
    this.driverFacingEvents = this.eventsConfig
      .filter((event) => event.isDriverFacing)
      .filter((event) => !EXCLUDED_EVENTS.includes(event.key));

    this.updateData();
    this.formValueChanges();
  }

  public ngOnChanges(): void {
    this.updateData();
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.unsubscribeOnDestroy.next();
    this.unsubscribeOnDestroy.complete();
  }

  public updateData() {
    if (
      this.enableInPersonEscalation !== undefined &&
      this.missedSelfCoachingSessions !== undefined &&
      this.minConsecutiveSelfCoachingSessions !== undefined
    ) {
      this.inPersonEscalationThresholdForm.setValue({
        enableInPersonEscalation: this.enableInPersonEscalation,
        missedSelfCoachingSessions: this.missedSelfCoachingSessions,
        minConsecutiveSelfCoachingSessions: this.minConsecutiveSelfCoachingSessions,
      });
      if (this.enableInPersonEscalation) {
        this.inPersonEscalationThresholdForm.get('missedSelfCoachingSessions')?.enable();
        this.inPersonEscalationThresholdForm.get('minConsecutiveSelfCoachingSessions')?.enable();
        this.updateEvents(this.escalationEventTypes);
        this.subscribeForCoachingThershold();
        this.disableEventSelection = false;
      } else {
        this.inPersonEscalationThresholdForm.get('missedSelfCoachingSessions')?.disable();
        this.inPersonEscalationThresholdForm.get('minConsecutiveSelfCoachingSessions')?.disable();
        this.disableEventSelection = true;
      }
      this.isEventsTypeValid = true;
    }
    this.valuesChanged = false;
    this.inPersonEscalationThresholdForm.markAsPristine();
    this.previousFormValue = {
      enableInPersonEscalation: this.enableInPersonEscalation,
      missedSelfCoachingSessions: this.missedSelfCoachingSessions,
      minConsecutiveSelfCoachingSessions: this.minConsecutiveSelfCoachingSessions,
    };
  }

  public selectEvent(e: Event) {
    console.log(e);
  }

  public checkEvent(e: MatCheckboxChange, event: string) {
    this.updateCheckedStatus(this.eventsConfig, event, e.checked);
    this.updateCheckedStatus(this.othersConfig, event, e.checked);
    this.updateCheckedStatus(this.customEvents, event, e.checked);
    this.trackEventCheckboxMixpanel(event, e.checked);
    this.checkIfDifferenceExists();
    this.updateValuesChanged();
  }

  private trackEventCheckboxMixpanel(eventKey: string, checked: boolean) {
    let category = '';
    let matchedEvent: configEvents | undefined;

    if (this.driverFacingEvents?.some((e) => e.key === eventKey)) {
      matchedEvent = this.driverFacingEvents.find((e) => e.key === eventKey);
      category = 'driver-facing-events';
    } else if (this.eventsConfig?.some((e) => e.key === eventKey)) {
      matchedEvent = this.eventsConfig.find((e) => e.key === eventKey);
      category = 'road-facing-events';
    } else if (this.customEvents?.some((e) => e.key === eventKey)) {
      matchedEvent = this.customEvents.find((e) => e.key === eventKey);
      category = 'custom-events';
    } else if (this.othersConfig?.some((e) => e.key === eventKey)) {
      matchedEvent = this.othersConfig.find((e) => e.key === eventKey);
      category = 'other-events';
    }
    if (!matchedEvent) {
      return;
    }
    const eventName = matchedEvent.label?.toLowerCase().replace(/\s+/g, '-');
    this.mixpanelService.track(`checkbox-toggled/configurations-page/coaching-page/in-person-escalations-card/${category}/${eventName}`, {
      EventKey: eventKey,
      Checked: checked,
    });
  }

  public subscribeForCoachingThershold() {
    const key = 'coachingConfig';
    const coachingThersholdValues = this.storageService.getStorageValue(key);
    this.coachingThersholdDirty = coachingThersholdValues;
  }

  private checkIfDifferenceExists() {
    const configArray = [...this.eventsConfig, ...this.othersConfig, ...this.customEvents];

    const escalationEventTypes = this.coachingThersholdDirty?.escalationEventTypes || [];

    let isEventTypeDifferenceExists = false;

    configArray.forEach((val: configEvents) => {
      let key = val.key === '0' ? 'panic-button' : val.key;

      if ((!escalationEventTypes.includes(key) && val.checked) || (escalationEventTypes.includes(key) && !val.checked)) {
        isEventTypeDifferenceExists = true;
      }
    });

    let isThresholdFormDifferenceExists = false;
    for (const key in this.inPersonEscalationThresholdForm.value) {
      if (this.coachingThersholdDirty?.[key] !== this.inPersonEscalationThresholdForm.value[key]) {
        isThresholdFormDifferenceExists = true;
      }
    }

    this.isEventsTypeValid = !(isEventTypeDifferenceExists || isThresholdFormDifferenceExists);
  }

  private formValueChanges() {
    this.inPersonEscalationThresholdForm.valueChanges.subscribe((currentValue) => {
      if (currentValue.enableInPersonEscalation !== this.previousFormValue?.enableInPersonEscalation) {
        this.mixpanelService.track(
          'toggle-switched/configurations-page/coaching-page/in-person-escalations-card/in-person-escalations-toggle',
          {
            Enabled: currentValue.enableInPersonEscalation,
          }
        );
      }

      if (currentValue.missedSelfCoachingSessions !== this.previousFormValue?.missedSelfCoachingSessions) {
        this.mixpanelService.track(
          'input-changed/configurations-page/coaching-page/in-person-escalations-card/self-coaching-sessions-missed',
          {
            MissedSelfCoachingSessions: currentValue.missedSelfCoachingSessions,
          }
        );
      }

      if (currentValue.minConsecutiveSelfCoachingSessions !== this.previousFormValue?.minConsecutiveSelfCoachingSessions) {
        this.mixpanelService.track('input-changed/configurations-page/coaching-page/in-person-escalations-card/recurring-risky-behavior', {
          MinConsecutiveSelfCoachingSessions: currentValue.minConsecutiveSelfCoachingSessions,
        });
      }

      this.previousFormValue = {
        enableInPersonEscalation: currentValue.enableInPersonEscalation,
        missedSelfCoachingSessions: currentValue.missedSelfCoachingSessions,
        minConsecutiveSelfCoachingSessions: currentValue.minConsecutiveSelfCoachingSessions,
      };
      this.updateValuesChanged();
    });
  }

  private updateValuesChanged(): void {
    const enableInPersonEscalation = this.inPersonEscalationThresholdForm.get('enableInPersonEscalation')?.value;
    const missedSelfCoachingSessions = this.inPersonEscalationThresholdForm.get('missedSelfCoachingSessions')?.value;
    const minConsecutiveSelfCoachingSessions = this.inPersonEscalationThresholdForm.get('minConsecutiveSelfCoachingSessions')?.value;

    const formChanged =
      enableInPersonEscalation !== this.enableInPersonEscalation ||
      missedSelfCoachingSessions !== this.missedSelfCoachingSessions ||
      minConsecutiveSelfCoachingSessions !== this.minConsecutiveSelfCoachingSessions;

    const currentSelected = [
      ...this.assetService.processSelectedEvents(this.eventsConfig),
      ...this.assetService.processSelectedEvents(this.othersConfig),
      ...this.assetService.processSelectedEvents(this.customEvents),
    ]
      .slice()
      .sort();

    const originalSelected = (this.escalationEventTypes || []).slice().sort();

    const eventsChanged = JSON.stringify(currentSelected) !== JSON.stringify(originalSelected);

    this.valuesChanged = formChanged || eventsChanged;
  }

  public onSubmit() {
    // this.gtmService.coachingThresholdSubmit();
    const coachingConfig = this.storageService.getStorageValue('coachingConfig');

    let selectedEvents = [
      ...this.assetService.processSelectedEvents(this.eventsConfig),
      ...this.assetService.processSelectedEvents(this.othersConfig),
      ...this.assetService.processSelectedEvents(this.customEvents),
    ];

    this.mixpanelService.track('button-clicked/configurations-page/coaching-page/in-person-escalations-card/submit', {
      'Change Values': this.inPersonEscalationThresholdForm.value,
    });

    const body = {
      ...this.inPersonEscalationThresholdForm.value,
      escalationEventTypes: selectedEvents,
      eventTypes: this.getSelectedKeys(coachingConfig?.eventTypes, this.getEvents(this.eventsConfig, this.othersConfig, this.customEvents)),
      coachingInterval: coachingConfig?.coachingInterval,
      incidentsPer100Miles: coachingConfig?.incidentsPer100Miles,
      minimumEventThreshold: coachingConfig?.minimumEventThreshold,
      enableSelfCoaching: this.enableSelfCoaching,
      enableAutoSelection: this.enableAutoSelection,
    };

    this.inPersonEscalationValues.emit(body);
  }

  private clearChecks(configArray: configEvents[]) {
    configArray?.forEach((element) => {
      element.checked = false;
    });
  }

  private clearAllCheckedStates() {
    this.clearChecks(this.eventsConfig);
    this.clearChecks(this.othersConfig);
    this.clearChecks(this.customEvents);
  }

  private updateEvents(selectedEvent: string[]) {
    this.clearAllCheckedStates();

    this.updateCheckedStatusInArray(this.eventsConfig, selectedEvent);
    this.updateCheckedStatusInArray(this.othersConfig, selectedEvent, true);
    this.updateCheckedStatusInArray(this.customEvents, selectedEvent);
  }

  private processConfig(configObject): configEvents[] {
    return Object.entries(configObject)
      .map(([key, value]: [string, configEvents]) => ({
        key,
        checked: false,
        ...value,
      }))
      .sort((a, b) => (a.label > b.label ? 1 : -1));
  }

  private updateCheckedStatus(configArray: configEvents[], key: string, checked: boolean) {
    configArray.forEach((element: configEvents) => {
      if (key === element.key) {
        element.checked = checked;
      }
    });
  }

  private updateCheckedStatusInArray(configArray: configEvents[], selectedEvent: string[], isOtherEvents?: boolean) {
    if (isOtherEvents) {
      configArray?.forEach((element: configEvents) => {
        if (element.key === '0') {
          if (selectedEvent?.includes('panic-button')) {
            element.checked = true;
          }
        }
      });
    } else {
      configArray?.forEach((element: configEvents) => {
        if (selectedEvent?.includes(element.key)) {
          element.checked = true;
        }
      });
    }
  }

  public toggleChanged(event: any) {
    if (event?.checked) {
      this.disableEventSelection = false;
      this.inPersonEscalationThresholdForm.get('enableInPersonEscalation')?.setValue(true);
      this.inPersonEscalationThresholdForm.get('missedSelfCoachingSessions')?.enable();
      this.inPersonEscalationThresholdForm.get('minConsecutiveSelfCoachingSessions')?.enable();
    } else {
      this.disableEventSelection = true;
      this.inPersonEscalationThresholdForm.get('enableInPersonEscalation')?.setValue(false);
      this.inPersonEscalationThresholdForm.get('missedSelfCoachingSessions')?.setValue(this.missedSelfCoachingSessions);
      this.inPersonEscalationThresholdForm.get('minConsecutiveSelfCoachingSessions')?.setValue(this.minConsecutiveSelfCoachingSessions);
      this.inPersonEscalationThresholdForm.get('missedSelfCoachingSessions')?.disable();
      this.inPersonEscalationThresholdForm.get('minConsecutiveSelfCoachingSessions')?.disable();
      this.updateEvents(this.escalationEventTypes);
      this.updateValuesChanged();
    }
  }

  private numberValidator(control: FormControl) {
    const value = control.value;
    if (!/^\d+$/.test(value)) {
      return { notANumber: true };
    }
    return null;
  }

  public getEvents(eventsConfigs: any, otherConfig: any, customevents: any): string[] {
    const standarkEventKeys = Object.entries(eventsConfigs).map(([_key, value]: [string, configEvents]) => value.key);
    const otherConfigKeys = Object.entries(otherConfig).map(([_key, value]: [string, configEvents]) =>
      value.key === '0' ? 'panic-button' : ''
    );
    const customEventsKeys = Object.entries(customevents).map(([_key, value]: [string, configEvents]) => value.key);
    return [...standarkEventKeys, ...otherConfigKeys, ...customEventsKeys];
  }

  private getSelectedKeys(storedArray: any[], autocompleted: any[]): string[] {
    const includedKeys: string[] = [];
    autocompleted.forEach((value) => {
      if (storedArray.includes(value)) {
        includedKeys.push(value);
      }
    });
    return includedKeys;
  }
}
